/**
 * "Ask the client" — composer + list.
 *
 * InfoRequestComposer: the adviser writes or DICTATES what they need (any topic — no rules;
 * the client is never forced, questions are optional unless marked required), or starts from a
 * list of missing items (resume gaps, the analysis' Missing Information, a job's open
 * questions). The AI turns it into a form, the adviser reviews/edits it, and the client gets a
 * no-login link by email. Optionally asks the client to review their resume on the same page.
 * Available to every adviser — opened from the conversation toolbar, the Case Dashboard and
 * the resume builder.
 *
 * InfoRequestsPanel: every request for the case — status, answers, resend, cancel, delete.
 * Answers also land in the case conversation (so analysis and resume drafting use them)
 * and the adviser is notified — that wiring is server-side.
 *
 * Backend: /info-requests/* on maven-platform-server; client page: platform.imavenai.com/info/:code
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import { useConfirm } from '../../../../Context/ConfirmContext';
import {
  Overlay, Shell, TopBar, Title, Spacer, Row, Field, Label, Hint, Input, TextArea, Select,
  Btn, IconBtn, Banner, Check, Entry,
} from './resumeStyles';
import { errMsg, isTokenError, fmtDateTime } from './resumeUtils';
import { useDictation, MicButton } from '../../../Helpers/Dictation';

const TYPES = [
  { value: 'text', label: 'Short answer' },
  { value: 'textarea', label: 'Long answer' },
  { value: 'date', label: 'Date' },
  { value: 'yesno', label: 'Yes / No' },
  { value: 'select', label: 'Choice' },
  { value: 'number', label: 'Number' },
];

const STATUS = {
  sent: { label: 'Sent', fg: '#93c5fd', bg: 'rgba(59,130,246,0.18)' },
  viewed: { label: 'Opened', fg: '#c4b5fd', bg: 'rgba(139,92,246,0.18)' },
  answered: { label: 'Answered', fg: '#86efac', bg: 'rgba(34,197,94,0.18)' },
  cancelled: { label: 'Cancelled', fg: '#cbd2e2', bg: 'rgba(148,163,184,0.18)' },
  expired: { label: 'Expired', fg: '#fcd34d', bg: 'rgba(245,158,11,0.18)' },
};

const Pill = styled.span`
  font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 999px; white-space: nowrap;
  color: ${p => STATUS[p.$s]?.fg || '#cbd2e2'}; background: ${p => STATUS[p.$s]?.bg || 'rgba(255,255,255,0.08)'};
`;

const newQuestion = (i) => ({ id: `q${Date.now().toString(36)}${i}`, question: '', type: 'text', options: [], helpText: '', required: false });

// ============================================================================
// COMPOSER
// ============================================================================

export function InfoRequestComposer({
  threadId, gaps = [], source = 'manual', sourceRefId, defaultEmail = '', defaultName = '',
  allowReview = false, defaultReview = false, postToConversation = true, onClose, onSent,
}) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  // Free-form flow starts by describing the need; list-driven flows (gaps) go straight to the form.
  const [step, setStep] = useState(gaps.length || defaultReview ? 'form' : 'describe');
  const [request, setRequest] = useState('');
  const [email, setEmail] = useState(defaultEmail || '');
  const [name, setName] = useState(defaultName || '');
  const [title, setTitle] = useState(defaultReview && !gaps.length ? 'Please review your resume' : 'A few questions from your adviser');
  const [message, setMessage] = useState('');
  const [language, setLanguage] = useState('en');
  const [questions, setQuestions] = useState([]);
  const [reviewResume, setReviewResume] = useState(!!defaultReview);
  const [days, setDays] = useState(14);
  const [composing, setComposing] = useState(false);
  const [sending, setSending] = useState(false);
  const dictation = useDictation({ onText: t => setRequest(v => (v.trim() ? `${v.trimEnd()} ${t}` : t)) });
  const [profileFields, setProfileFields] = useState([]);
  useEffect(() => {
    api.get(`/info-requests/case/${threadId}/profile-fields`)
      .then(r => setProfileFields(r.data.data.fields || []))
      .catch(() => setProfileFields([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  /** AI: the adviser's words (or the gaps list) -> title, intro and questions. */
  const compose = useCallback(async (lang = language) => {
    const body = request.trim() ? { request: request.trim() } : { gaps };
    if (!body.request && !gaps.length) { showToast('Describe what you need from the client', 'warning'); return; }
    setComposing(true);
    try {
      const r = await api.post(`/info-requests/case/${threadId}/compose`, { ...body, clientName: name, language: lang });
      const d = r.data.data;
      setQuestions(d.questions || []);
      if (d.title) setTitle(d.title);
      if (d.message) setMessage(d.message);
      setStep('form');
      return d;
    } catch (e) {
      if (!isTokenError(e)) showToast(errMsg(e, 'Could not create the form — you can add questions by hand'), 'error');
      if (gaps.length) {
        setQuestions(q => (q.length ? q : gaps.map((g, i) => ({ ...newQuestion(i), question: g }))));
        setStep('form');
      }
      return null;
    } finally { setComposing(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, gaps, request, name, language]);

  useEffect(() => {
    if (gaps.length) compose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (i, p) => setQuestions(qs => qs.map((q, k) => (k === i ? { ...q, ...p } : q)));
  const move = (i, d) => setQuestions(qs => {
    const j = i + d; if (j < 0 || j >= qs.length) return qs;
    const n = [...qs]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const emailOk = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const send = async (composed) => {
    const qs = (composed?.questions || questions).filter(q => q.question.trim());
    const sendTitle = composed?.title || title;
    const sendMessage = composed ? (composed.message || message) : message;
    if (!qs.length && !reviewResume) return showToast('Add at least one question', 'warning');
    if (!emailOk()) return showToast('Enter the client\'s email', 'warning');
    if (qs.some(q => q.type === 'select' && !(q.options || []).filter(Boolean).length)) return showToast('Give every "Choice" question its options', 'warning');
    setSending(true);
    try {
      const r = await api.post(`/info-requests/case/${threadId}`, {
        questions: qs, recipientEmail: email.trim(), recipientName: name.trim(), title: sendTitle, message: sendMessage,
        language, source, sourceRefId, reviewResume, expiresInDays: days, postToConversation,
      });
      if (r.data.data.emailFailed) showToast('Saved, but the email failed — use "Resend" or copy the link', 'warning');
      else showToast(`Sent to ${email.trim()}`, 'success');
      onSent && onSent(r.data.data.request);
      onClose();
    } catch (e) { showToast(errMsg(e, 'Could not send'), 'error'); }
    finally { setSending(false); }
  };

  /** One click: build the form and email it straight away (the adviser can still see it in Client follow-ups). */
  const createAndSend = async () => {
    if (!emailOk()) return showToast('Enter the client\'s email first', 'warning');
    const d = await compose();
    if (d && d.questions?.length) await send(d);
  };

  const recipientRow = (
    <Row>
      <Field><Label>Client email</Label><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="client@example.com" /></Field>
      <Field><Label>Client name</Label><Input value={name} onChange={e => setName(e.target.value)} /></Field>
      <Field $min="120px"><Label>Language</Label>
        <Select value={language} onChange={e => setLanguage(e.target.value)}>
          <option value="en">English</option><option value="fr">Français</option>
        </Select>
      </Field>
    </Row>
  );

  return createPortal(
    <Overlay style={{ zIndex: 5200 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <Shell style={{ maxWidth: 860 }}>
        <TopBar>
          <Title>Request information from the client</Title>
          <Spacer />
          {step === 'describe' ? (
            <>
              <Btn $variant="ghost" onClick={() => compose()} disabled={composing || sending || !request.trim()}
                title="Create the form and let me check it before it is sent">
                {composing ? 'Creating…' : '✨ Create & review'}
              </Btn>
              <Btn $variant="ai" onClick={createAndSend} disabled={composing || sending || !request.trim()}
                title="Create the form and email it to the client right away">
                {sending ? 'Sending…' : composing ? 'Creating…' : '✨ Create & send'}
              </Btn>
            </>
          ) : (
            <Btn onClick={() => send()} disabled={sending || composing}>{sending ? 'Sending…' : 'Send to client'}</Btn>
          )}
          <IconBtn title="Close" onClick={onClose}>×</IconBtn>
        </TopBar>
        <div style={{ overflowY: 'auto', padding: 18 }}>
          {step === 'describe' ? (
            <>
              <Hint $mb>Write or dictate what you need from the client, in your own words — any information or documents.
                The AI turns it into a simple form and emails it to the client (no login needed) — “Create &amp; send” sends it right away, “Create &amp; review” lets you check it first.
                Every question is optional for the client unless you make it required.</Hint>
              {recipientRow}
              <Row $mt style={{ alignItems: 'center' }}>
                <Label style={{ flex: 1 }}>What do you need?</Label>
                <MicButton dictation={dictation} />
              </Row>
              <TextArea $h={200} style={{ marginTop: 8 }} value={request} onChange={e => setRequest(e.target.value)}
                placeholder={'e.g. I need his date of birth, his wife\'s full name and date of birth, the kids\' names and ages, whether he was ever refused a visa, his passport number and expiry, and when he can send his police certificate.'} />
              <Hint style={{ marginTop: 8 }}>{dictation.state === 'transcribing' ? 'Transcribing your recording…' : 'Tip: dictating works well — speak naturally, then fix anything before creating the form.'}</Hint>
            </>
          ) : (
            <>
              <Hint $mb>The client gets an email with a link — no login needed. Their answers come back into this case and are
                saved to the client's data: each answer goes to the field shown in “Saves to” (the same fields the case analysis fills,
                used by forms and autofill); the rest is kept as additional info. Questions are optional unless marked required.</Hint>
              {recipientRow}
              <Row $mt>
                <Field $grow={2}><Label>Title (the button in the email)</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></Field>
                <Field $min="120px"><Label>Link valid for (days)</Label><Input type="number" min="1" max="60" value={days} onChange={e => setDays(e.target.value)} /></Field>
              </Row>
              <Row $mt>
                <Field><Label>Note to the client (optional)</Label>
                  <TextArea $h={60} value={message} onChange={e => setMessage(e.target.value)} placeholder="Why you're asking — shown at the top of the form and in the email." />
                </Field>
              </Row>
              {allowReview && (
                <Row $mt>
                  <Check><input type="checkbox" checked={reviewResume} onChange={e => setReviewResume(e.target.checked)} />
                    Also ask the client to review their resume (approve or request changes)</Check>
                </Row>
              )}

              <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Label style={{ fontSize: 14, color: '#fff' }}>Questions ({questions.length})</Label>
                {request.trim() && <Btn $sm $variant="ghost" onClick={() => setStep('describe')} disabled={composing}>← Edit description</Btn>}
                {(request.trim() || gaps.length > 0) && <Btn $sm $variant="ghost" onClick={() => compose()} disabled={composing}>{composing ? 'Creating…' : '✨ Re-create form'}</Btn>}
              </div>
              {composing && <Banner style={{ marginTop: 10 }}>Creating the form…</Banner>}

              <div style={{ marginTop: 10 }}>
                {questions.map((q, i) => (
                  <Entry key={q.id}>
                    <Row style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                      <span style={{ color: '#8b93a7', fontSize: 12, paddingTop: 9, width: 18 }}>{i + 1}.</span>
                      <Field $grow={3}><TextArea $h={44} value={q.question} placeholder="Question for the client" onChange={e => patch(i, { question: e.target.value })} /></Field>
                      <Field $min="130px"><Select value={q.type} onChange={e => patch(i, { type: e.target.value })}>{TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</Select></Field>
                      <IconBtn title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</IconBtn>
                      <IconBtn title="Move down" disabled={i === questions.length - 1} onClick={() => move(i, 1)}>↓</IconBtn>
                      <IconBtn title="Remove" $danger onClick={() => setQuestions(qs => qs.filter((_, k) => k !== i))}>×</IconBtn>
                    </Row>
                    <Row $mt style={{ paddingLeft: 28, alignItems: 'center' }}>
                      {q.type === 'select' && (
                        <Field $grow={2}><Input value={q.optionsText ?? (q.options || []).join(', ')} placeholder="Options, comma-separated"
                          onChange={e => patch(i, { optionsText: e.target.value, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} /></Field>
                      )}
                      <Field $grow={2}><Input value={q.helpText || ''} placeholder="Hint shown under the question (optional)" onChange={e => patch(i, { helpText: e.target.value })} /></Field>
                      <Field $min="190px" title="Where the answer is saved in the client's data">
                        <Select value={q.profileKey || ''} onChange={e => patch(i, { profileKey: e.target.value || undefined })}>
                          <option value="">Saves to: additional info</option>
                          {profileFields.map(f => <option key={f.key} value={f.key}>Saves to: {f.label}</option>)}
                        </Select>
                      </Field>
                      <Check title="The client must answer this one"><input type="checkbox" checked={q.required === true} onChange={e => patch(i, { required: e.target.checked })} />Required</Check>
                    </Row>
                  </Entry>
                ))}
                <Btn $sm $variant="ghost" onClick={() => setQuestions(qs => [...qs, newQuestion(qs.length)])}>+ Add question</Btn>
              </div>
            </>
          )}
        </div>
      </Shell>
    </Overlay>,
    document.body
  );
}

// ============================================================================
// LIST
// ============================================================================

export function InfoRequestsPanel({ threadId, refreshKey, onAsk }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/info-requests/case/${threadId}`);
      setRows(r.data.data.requests || []);
    } catch (e) { setRows([]); showToast(errMsg(e, 'Could not load follow-ups'), 'error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (r, action) => {
    if (action === 'cancel' && !(await confirm({ title: 'Cancel this request?', message: 'The link stops working for the client.', confirmText: 'Cancel request', type: 'warning' }))) return;
    if (action === 'delete' && !(await confirm({ title: 'Delete this request?', message: 'Answers already posted into the conversation stay there.', confirmText: 'Delete', type: 'danger' }))) return;
    setBusy(r.id + action);
    try {
      if (action === 'delete') await api.delete(`/info-requests/${r.id}`);
      else await api.post(`/info-requests/${r.id}/${action}`);
      showToast(action === 'resend' ? 'Reminder sent' : action === 'cancel' ? 'Cancelled' : 'Deleted', 'success');
      await load();
    } catch (e) { showToast(errMsg(e, 'Action failed'), 'error'); }
    finally { setBusy(''); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); showToast('Link copied', 'success'); }
    catch { showToast(url, 'info'); }
  };

  if (rows === null) return <Hint>Loading…</Hint>;

  return (
    <div>
      <Row style={{ alignItems: 'center', marginBottom: 12 }}>
        <Hint style={{ flex: 1 }}>Questions you sent the client, and their answers. Answers are also posted into the conversation.</Hint>
        {onAsk && <Btn $sm onClick={onAsk}>+ Ask the client</Btn>}
      </Row>
      {!rows.length && <Banner>No follow-ups yet. Use “Ask the client” to send a few questions by email.</Banner>}
      {rows.map(r => (
        <Entry key={r.id}>
          <Row style={{ alignItems: 'center', flexWrap: 'nowrap', cursor: 'pointer' }} onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))}>
            <Pill $s={r.status}>{STATUS[r.status]?.label || r.status}</Pill>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#e6e9f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
              <div style={{ fontSize: 12, color: '#8b93a7' }}>
                {r.questions.length} question{r.questions.length === 1 ? '' : 's'}{r.reviewResume ? ' + resume review' : ''} · {r.recipientEmail} · sent {fmtDateTime(r.sentAt)}
                {r.answeredAt ? ` · answered ${fmtDateTime(r.answeredAt)}` : ''}
              </div>
            </div>
            <span style={{ color: '#8b93a7' }}>{open[r.id] ? '▾' : '▸'}</span>
          </Row>
          {open[r.id] && (
            <div style={{ marginTop: 10 }}>
              {r.questions.map(q => (
                <div key={q.id} style={{ margin: '8px 0', fontSize: 13 }}>
                  <div style={{ color: '#aeb6ca' }}>{q.question}</div>
                  <div style={{ color: q.answer ? '#e6e9f0' : '#6b7280', paddingLeft: 10, whiteSpace: 'pre-wrap' }}>→ {q.answer || (r.status === 'answered' ? '(no answer)' : 'waiting…')}</div>
                </div>
              ))}
              {r.savedToProfile && (r.savedToProfile.fields?.length > 0 || r.savedToProfile.additional?.length > 0) && (
                <Banner style={{ marginTop: 8 }}>
                  <b>Saved to the client's data</b>
                  <ul>
                    {(r.savedToProfile.fields || []).map(f => (
                      <li key={f.key}>{f.label}: <b>{f.value}</b>{f.previous ? <span style={{ color: '#93a3c0' }}> (was: {f.previous})</span> : null}</li>
                    ))}
                    {r.savedToProfile.additional?.length > 0 && (
                      <li>{r.savedToProfile.additional.length} more answer{r.savedToProfile.additional.length > 1 ? 's' : ''} kept as additional info</li>
                    )}
                  </ul>
                </Banner>
              )}
              {r.review?.decision && (
                <Banner $tone={r.review.decision === 'approved' ? undefined : 'warn'} style={{ marginTop: 8 }}>
                  {r.review.decision === 'approved' ? '✅ The client approved the resume.' : <>✏️ The client asked for changes: <b>{r.review.comment}</b></>}
                </Banner>
              )}
              <Row $mt>
                <Btn $sm $variant="ghost" onClick={() => copy(r.url)}>Copy link</Btn>
                {['sent', 'viewed', 'expired'].includes(r.status) && <Btn $sm $variant="ghost" disabled={!!busy} onClick={() => act(r, 'resend')}>{r.status === 'expired' ? 'Re-open & resend' : 'Send reminder'}</Btn>}
                {['sent', 'viewed'].includes(r.status) && <Btn $sm $variant="ghost" disabled={!!busy} onClick={() => act(r, 'cancel')}>Cancel</Btn>}
                <Btn $sm $variant="ghost" disabled={!!busy} onClick={() => act(r, 'delete')} style={{ color: '#fca5a5' }}>Delete</Btn>
              </Row>
            </div>
          )}
        </Entry>
      ))}
    </div>
  );
}
